import { createUseVariantStyle } from '@bonusly/ds-hooks';
import { rem } from '@bonusly/ds-utils';

import { ButtonColorScheme, ButtonSize, ButtonVariant } from '../types';

interface UseVariantStyleProps {
	colorScheme?: ButtonColorScheme;
	size?: ButtonSize;
	variant?: ButtonVariant;
}

const switchColor = (props: { colorScheme?: ButtonColorScheme }) => {
	const color = (primary: string, secondary: string) => (props.colorScheme === 'primary' ? primary : secondary);

	color.toString = () => props.colorScheme;

	return color;
};

export const useVariantStyle = createUseVariantStyle<UseVariantStyleProps>({
	size: {
		xs: {
			borderRadius: 'sm',
			fontSize: 'sm',
			h: rem(24),
			lineHeight: 1.43,
			px: 2,
		},
		sm: {
			borderRadius: 'sm',
			fontSize: 'md',
			h: rem(32),
			px: 3,
		},
		md: {
			borderRadius: 'md',
			fontSize: 'md',
			h: rem(40),
			px: 4,
		},
		lg: {
			borderRadius: 'md',
			fontSize: 'lg',
			h: rem(44),
			lineHeight: 1.56,
			px: 4,
		},
		xl: {
			borderRadius: 'md',
			fontSize: 'lg',
			h: rem(48),
			px: 5,
		},
	},
	variant: {
		solid: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: color('primary.bg', 'text.default'),
				color: 'text.onColor',
				_hover: {
					bg: `${color}.bgHover`,
					_disabled: {
						bg: 'secondary.bgAlpha',
						color: 'text.disabled',
					},
				},
				_active: {
					bg: `${color}.bgActive`,
				},
				_disabled: {
					bg: 'secondary.bgAlpha',
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
		alpha: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: `${color}.bgAlpha`,
				color: color('primary.fgStrong', 'secondary.fg'),
				_hover: {
					bg: `${color}.bgAlphaHover`,
					color: color('primary.fgStronger', 'secondary.fgActive'),
					_disabled: {
						bg: 'secondary.bgAlpha',
						color: 'text.disabled',
						cursor: 'not-allowed',
					},
				},
				_active: {
					bg: `${color}.bgAlphaActive`,
					color: color('primary.fgStronger', 'secondary.fgActive'),
				},
				_disabled: {
					bg: 'secondary.bgAlpha',
					color: 'text.disabled',
				},
			};
		},
		ghost: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: 'transparent',
				color: color('primary.fgStrong', 'secondary.fg'),
				_hover: {
					bg: `${color}.bgAlpha`,
					color: color('primary.fgStronger', 'secondary.fgActive'),
					_disabled: {
						bg: 'transparent',
						color: 'text.disabled',
					},
				},
				_active: {
					bg: `${color}.bgAlphaHover`,
					color: color('primary.fgStronger', 'secondary.fgActive'),
				},
				_disabled: {
					bg: 'transparent',
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
		link: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: 'transparent',
				color: color('primary.fg', 'text.softest'),
				h: 'auto',
				p: 0,
				verticalAlign: 'baseline',
				_hover: {
					color: color('primary.fgStrong', 'text.default'),
					textDecoration: 'underline',
					_disabled: {
						color: 'text.disabled',
					},
				},
				_active: {
					color: color('primary.fgStronger', 'text.default'),
				},
				_disabled: {
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
		outline: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: 'transparent',
				color: color('primary.fgStrong', 'secondary.fg'),
				borderColor: color('primary.fgStrong', 'secondary.fg'),
				borderWidth: 'thin',
				_hover: {
					bg: `${color}.bgAlpha`,
					color: color('primary.fgStronger', 'secondary.fgActive'),
					_disabled: {
						bg: 'transparent',
						color: 'text.disabled',
					},
				},
				_active: {
					bg: `${color}.bgAlphaHover`,
					color: color('primary.fgStronger', 'secondary.fgActive'),
				},
				_disabled: {
					bg: 'transparent',
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
		subtle: () => {
			return {
				bg: 'bg.subtle',
				color: 'text.default',
				_hover: {
					bg: 'gray.100',
					color: 'text.default',
					_disabled: {
						bg: 'bg.subtle',
						color: 'text.disabled',
					},
				},
				_active: {
					bg: 'gray.200',
					color: 'text.default',
				},
				_disabled: {
					bg: 'bg.subtle',
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
	},
});
